import "ag-grid-enterprise";
import { Badge, Box, Group, Pagination, Select, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { useMemo } from "react";
import { openapi, type PostMultiplacerDrofusInterfaceProjectChangeEventsApiResponse } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DrofusDisciplineBadge } from "../components/DrofusDisciplineBadge";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import { DrofusForekomstDetailsModal } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import {
  closeDrofusEndringerOccurrenceDetails,
  openDrofusEndringerOccurrenceDetails,
  selectDrofusEndringerApiArg,
  selectDrofusEndringerPagination,
  selectDrofusEndringerSelectedOccurrenceRef,
  setDrofusEndringerLimit,
  setDrofusEndringerOffset,
} from "./drofusEndringerSlice";

type DrofusEndringRow =
  PostMultiplacerDrofusInterfaceProjectChangeEventsApiResponse["items"][number];

const changeTypeLabel: Record<DrofusEndringRow["changeType"], string> = {
  updated: "Endret",
  added: "Ny",
  removed: "Fjernet",
};

const changeTypeColor: Record<DrofusEndringRow["changeType"], string> = {
  updated: "yellow",
  added: "green",
  removed: "red",
};

const defaultColDef: ColDef<DrofusEndringRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  minWidth: 120,
};

const columnsOnlySideBar = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Kolonner",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
};

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

function getOccurrenceId(row?: DrofusEndringRow): number | null {
  if (!row || row.entityType !== "occurrence") return null;
  if (typeof row.entityId === "number") return row.entityId;

  const numericId = Number(row.entityId);
  return Number.isInteger(numericId) ? numericId : null;
}

const ChangeTypeRenderer: React.FC<ICellRendererParams<DrofusEndringRow, DrofusEndringRow["changeType"]>> = ({
  value,
}) => {
  if (!value) return null;
  return (
    <Badge size="sm" radius="sm" color={changeTypeColor[value]} variant="light">
      {changeTypeLabel[value]}
    </Badge>
  );
};

const DisciplineRenderer: React.FC<ICellRendererParams<DrofusEndringRow, string[]>> = ({ value }) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <DrofusDisciplineBadge key={discipline} discipline={discipline} />
      ))}
    </Group>
  );
};

const OccurrenceIdRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow, number | null> & {
    onOpenDetails?: (row: DrofusEndringRow, occurrenceId: number) => void;
  }
> = ({ value, data, onOpenDetails }) => {
  if (!data || !value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenDetails?.(data, value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

const RoomNumberRenderer: React.FC<
  ICellRendererParams<DrofusEndringRow, string | null> & {
    onOpenRoom?: (roomNumber: string) => void;
  }
> = ({ value, onOpenRoom }) => {
  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpenRoom?.(value)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

export const DrofusEndringerTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const apiArg = useAppSelector(selectDrofusEndringerApiArg);
  const pagination = useAppSelector(selectDrofusEndringerPagination);
  const selectedOccurrenceRef = useAppSelector(selectDrofusEndringerSelectedOccurrenceRef);
  const { data, isLoading, isFetching, isError } =
    openapi.usePostMultiplacerDrofusInterfaceProjectChangeEventsQuery(apiArg ?? skipToken);

  const rowData = data?.items ?? [];

  const columnDefs = useMemo<ColDef<DrofusEndringRow>[]>(
    () => [
      {
        headerName: "Dato",
        field: "runStartedAt",
        width: 150,
        valueFormatter: ({ value }: ValueFormatterParams<DrofusEndringRow, string>) =>
          formatDateTime(value),
      },
      {
        headerName: "Type",
        field: "changeType",
        width: 120,
        cellRenderer: "ChangeTypeRenderer",
      },
      {
        headerName: "Forekomst ID",
        colId: "occurrenceId",
        width: 140,
        valueGetter: ({ data }) => getOccurrenceId(data),
        cellRenderer: "OccurrenceIdRenderer",
        cellRendererParams: {
          onOpenDetails: (row: DrofusEndringRow, occurrenceId: number) =>
            dispatch(
              openDrofusEndringerOccurrenceDetails({
                projectNo: row.projectNo,
                runId: row.runId,
                occurrenceId,
              })
            ),
        },
      },
      {
        headerName: "Fag",
        field: "affectedDisciplines",
        width: 150,
        cellRenderer: "DisciplineRenderer",
        valueFormatter: ({ value }: ValueFormatterParams<DrofusEndringRow, string[]>) =>
          Array.isArray(value) ? value.join(", ") : "",
      },
      {
        headerName: "Romnummer",
        colId: "roomFunctionNo",
        width: 140,
        valueGetter: ({ data }) => data?.context.room?.functionNo,
        cellRenderer: "RoomNumberRenderer",
        cellRendererParams: {
          onOpenRoom: (roomNumber: string) => {
            dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_INFO));
            dispatch(setSelectedSpaceNumberInRedux(roomNumber));
          },
        },
      },
      {
        headerName: "Romnavn",
        colId: "roomName",
        minWidth: 180,
        flex: 1,
        valueGetter: ({ data }) => data?.context.room?.fullName,
      },
      {
        headerName: "Bygg",
        field: "roomBuilding",
        width: 110,
      },
      {
        headerName: "Etasje",
        field: "roomFloor",
        width: 130,
      },
      {
        headerName: "CompCode",
        colId: "compCode",
        width: 140,
        valueGetter: ({ data }) => data?.context.article?.compCode,
      },
      {
        headerName: "Artikkelnavn",
        colId: "articleName",
        minWidth: 240,
        flex: 1,
        valueGetter: ({ data }) => data?.context.article?.name,
      },
      {
        headerName: "Felt",
        field: "displayName",
        minWidth: 220,
        flex: 1,
        valueGetter: ({ data }) => data?.displayName ?? data?.normalizedPath ?? data?.changeKey,
      },
      {
        headerName: "Gammel verdi",
        field: "oldValue",
        minWidth: 180,
        flex: 1,
        valueFormatter: ({ value }) => formatValue(value),
      },
      {
        headerName: "Ny verdi",
        field: "newValue",
        minWidth: 180,
        flex: 1,
        valueFormatter: ({ value }) => formatValue(value),
      },
    ],
    [dispatch]
  );

  return (
    <Box h="100%" w="100%" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DrofusForekomstDetailsModal
        occurrenceRef={selectedOccurrenceRef}
        opened={selectedOccurrenceRef != null}
        onClose={() => dispatch(closeDrofusEndringerOccurrenceDetails())}
      />

      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text size="sm" c={isError ? "red" : "dimmed"} style={{ whiteSpace: "nowrap" }}>
          {isError
            ? "Kunne ikke laste endringer"
            : `${pagination.firstVisibleRow}-${pagination.lastVisibleRow} av ${pagination.total}`}
        </Text>
        {isFetching && (
          <Badge variant="light" color="blue" radius="sm">
            Laster
          </Badge>
        )}
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<DrofusEndringRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading}
          animateRows
          sideBar={columnsOnlySideBar}
          suppressScrollOnNewData
          tooltipShowDelay={150}
          components={{
            ChangeTypeRenderer,
            DisciplineRenderer,
            OccurrenceIdRenderer,
            RoomNumberRenderer,
          }}
          overlayNoRowsTemplate="<span style='padding:8px;display:inline-block;'>Ingen endringer funnet</span>"
        />
      </Box>

      <Group justify="space-between" align="center" mt="sm" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Rader per side
          </Text>
          <Select
            size="xs"
            w={90}
            value={String(pagination.limit)}
            data={["50", "100", "250", "500"]}
            disabled={!apiArg || isFetching}
            onChange={(value) => {
              const nextLimit = Number(value);
              if (Number.isFinite(nextLimit) && nextLimit > 0) {
                dispatch(setDrofusEndringerLimit(nextLimit));
              }
            }}
          />
        </Group>
        <Pagination
          size="sm"
          total={pagination.totalPages}
          value={pagination.activePage}
          disabled={!apiArg || isFetching}
          onChange={(page) => dispatch(setDrofusEndringerOffset((page - 1) * pagination.limit))}
        />
      </Group>
    </Box>
  );
};
